import { BufferAttribute, BufferGeometry, Line3, Points, Vector3 } from "three";
import { StyleUtils, VertexSemantic } from "../../../utils/internal";
import { GeometryNode } from "./internal";
/**
 * Creates a point geometry
 *
 * @class PointNode
 * @extends {SceneGraphNode}
 * @constructor
 */
export class PointNode extends GeometryNode {
    /**
     * @method constructor
     * @param {number[]} coordinates position of box in lv95 [m]
     * @param {number} size - the sidelength in [m]
     */
    constructor(coordinates, materialDefinition, parent) {
        super(null);
        this.is2dfeature = false;
        this.parent = parent;
        this.coordinates = coordinates.slice();
        if (coordinates.length == 2) {
            this.is2dfeature = true;
            this.coordinates[2] = 0.0;
        }
        this._centroid = [0, 0, 0];
        this.vertexSemantic = new VertexSemantic("v");
        this.materialDefinition = materialDefinition.point;
    }
    init(traversalState) {
        super.init(traversalState);
        const engine = traversalState.scene.engine;
        this.geometry = new BufferGeometry();
        const material = StyleUtils.CreatePointMaterial(this.materialDefinition, engine, null, false, (texture) => {
            this.styletextureloaded(texture);
        });
        this.object3D = new Points(this.geometry, material);
        this.iMesh = this.object3D;
        this.iMesh.visible = this.parent.isVisible();
        this.needsupdate = true;
    }
    render(traversalState) {
        super.render(traversalState);
        if (this.object3D != null)
            if (!this.object3D.visible)
                return;
        if (this.is2dfeature) {
            //calc the height if no height is given
            if (traversalState.groundPlaneUpdated || this.needsupdate) {
                //update the z values
                const p1 = new Vector3(this.coordinates[0], this.coordinates[1], -8000);
                const p2 = new Vector3(this.coordinates[0], this.coordinates[1], 8000);
                const l1 = new Line3(p1, p2);
                const target = new Vector3();
                const pi = traversalState.groundPlane.intersectLine(l1, target);
                if (pi != null) {
                    this.coordinates[2] = pi.z;
                }
                this.needsupdate = true;
            }
        }
        if (this._centroid != traversalState.scene.engine.centroid || this.needsupdate) {
            this.needsupdate = false;
            this._centroid = traversalState.scene.engine.centroid;
            const colors = [1, 0, 0];
            const positions = [
                this.coordinates[0] - this._centroid[0] || 0,
                this.coordinates[1] - this._centroid[1] || 0,
                this.coordinates[2] - this._centroid[2] || 0,
            ];
            // common attirbutes
            this.geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
            this.geometry.attributes["position"].needsUpdate = true;
            if (this.vertexSemantic.hasColor) {
                // vertex colors
                this.geometry.setAttribute("color", new BufferAttribute(new Float32Array(colors), 3));
                this.geometry.attributes["color"].needsUpdate = true;
            }
        }
        this.geometry.computeBoundingSphere();
    }
    show() {
        this.object3D.visible = true;
    }
    hide() {
        this.object3D.visible = false;
    }
    setStyle(materialDefinition) {
        this.materialDefinition = materialDefinition.point;
        this.iMesh.material = StyleUtils.CreatePointMaterial(this.materialDefinition, this.currentTraversalState.scene.engine, null, false, (texture) => {
            this.styletextureloaded(texture);
        });
        this.iMesh.material.needsUpdate = true;
    }
    pick(mx, my) {
        return this.currentTraversalState.rayCaster.intersectObject(this.iMesh);
    }
    updateCoordinates(coordinates) {
        if (this.is2dfeature)
            coordinates[2] = 0.0;
        this.coordinates = coordinates;
        this.needsupdate = true;
    }
    destroy() {
        this.coordinates = null;
        this._centroid = null;
        this.geometry = null;
        this.object3D = null;
        this.iMesh = null;
        this.vertexSemantic = null;
        this.parent = null;
        this.lastpickinfo = null;
    }
}
//# sourceMappingURL=pointnode.js.map